/**
 * Bulk Operation Form Component
 * Form for creating new bulk invoice operations
 */

import React, { useState, useEffect } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { enhancedInvoiceService } from '../../services/enhancedInvoiceService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './BulkOperationForm.css';

const BulkOperationForm = ({ onSuccess, onCancel }) => {
  const { showSuccess, showError } = useNotifications();
  const { canManageInvoices } = useRBAC();

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    operation_type: 'annual_renewal',
    description: '',
    financial_period: '',
    invoice_amount: 50.00,
    due_days: 30,
    send_immediately: false,
    send_emails: true,
    member_ids: []
  });
  const [errors, setErrors] = useState({});
  const [financialPeriods, setFinancialPeriods] = useState([]);

  useEffect(() => {
    const loadFinancialPeriods = async () => {
      try {
        setIsLoading(true);
        // TODO: Replace with actual API call to load financial periods
        // const response = await financialPeriodService.getFinancialPeriods();
        // if (response.success) {
        //   setFinancialPeriods(response.data);
        // }
        setFinancialPeriods([]);
      } catch (error) {
        showError('Failed to load financial periods', 'Error');
      } finally {
        setIsLoading(false);
      }
    };

    loadFinancialPeriods();
  }, [showError]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const validation = enhancedInvoiceService.validateBulkOperationData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsProcessing(true);
    try {
      const formattedData = enhancedInvoiceService.formatBulkOperationDataForAPI(formData);
      const response = await enhancedInvoiceService.createBulkOperation(formattedData);

      if (response.success) {
        showSuccess('Bulk operation created successfully', 'Success');
        onSuccess();
      } else {
        showError(response.message || 'Failed to create operation', 'Error');
      }
    } catch (error) {
      showError('Failed to create operation', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bulk-operation-form">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-operation-form">
      <div className="form-header">
        <h2>Create Bulk Invoice Operation</h2>
        <p>Set up a new bulk invoice operation for multiple members</p>
      </div>

      <form onSubmit={handleSubmit} className="operation-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label className="form-label">
              Operation Title *
            </label>
            <input
              type="text"
              className={`form-input ${errors.title ? 'error' : ''}`}
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Annual Renewal 2024"
              disabled={isProcessing}
            />
            {errors.title && (
              <span className="form-error">{errors.title}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Operation Type *
            </label>
            <select
              className={`form-select ${errors.operation_type ? 'error' : ''}`}
              value={formData.operation_type}
              onChange={(e) => handleInputChange('operation_type', e.target.value)}
              disabled={isProcessing}
            >
              <option value="annual_renewal">Annual Renewal</option>
              <option value="new_member">New Member</option>
              <option value="custom">Custom</option>
            </select>
            {errors.operation_type && (
              <span className="form-error">{errors.operation_type}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Description
            </label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Optional description for this operation"
              rows={3}
              disabled={isProcessing}
            />
          </div>
        </div>

        {/* Financial Details */}
        <div className="form-section">
          <h3>Financial Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Financial Period *
              </label>
              <select
                className={`form-select ${errors.financial_period ? 'error' : ''}`}
                value={formData.financial_period}
                onChange={(e) => handleInputChange('financial_period', e.target.value)}
                disabled={isProcessing}
              >
                <option value="">Select Financial Period</option>
                {financialPeriods.map(period => (
                  <option key={period.id} value={period.name}>
                    {period.name}
                  </option>
                ))}
              </select>
              {errors.financial_period && (
                <span className="form-error">{errors.financial_period}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Invoice Amount *
              </label>
              <input
                type="number"
                className={`form-input ${errors.invoice_amount ? 'error' : ''}`}
                value={formData.invoice_amount}
                onChange={(e) => handleInputChange('invoice_amount', parseFloat(e.target.value))}
                min="0"
                step="0.01"
                disabled={isProcessing}
              />
              {errors.invoice_amount && (
                <span className="form-error">{errors.invoice_amount}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Due Days *
              </label>
              <input
                type="number"
                className={`form-input ${errors.due_days ? 'error' : ''}`}
                value={formData.due_days}
                onChange={(e) => handleInputChange('due_days', parseInt(e.target.value))}
                min="1"
                max="365"
                disabled={isProcessing}
              />
              {errors.due_days && (
                <span className="form-error">{errors.due_days}</span>
              )}
            </div>
          </div>
        </div>

        {/* Operation Settings */}
        <div className="form-section">
          <h3>Operation Settings</h3>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.send_immediately}
                onChange={(e) => handleInputChange('send_immediately', e.target.checked)}
                disabled={isProcessing}
              />
              <span className="checkbox-label">Send Immediately</span>
            </label>
            <span className="form-hint">
              Start processing the operation immediately after creation
            </span>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.send_emails}
                onChange={(e) => handleInputChange('send_emails', e.target.checked)}
                disabled={isProcessing}
              />
              <span className="checkbox-label">Send Email Notifications</span>
            </label>
            <span className="form-hint">
              Send email notifications to members about their invoices
            </span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isProcessing || !canManageInvoices}
          >
            {isProcessing ? 'Creating...' : 'Create Operation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BulkOperationForm;
